import { useOktaAuth } from '@okta/okta-react';
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { App } from 'antd';
import { useEffect, useState } from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import {
	APPS_API_URL,
	AUTH_SERVICES_BASE_URL,
	PRINCIPALS_API_URL,
} from '@constants/url';
import useToken from '@hooks/useToken';
import useTopbar from '@hooks/useTopbar';
import { getFetch } from '@utils/get-fetch';
import { postFetchWithRenewal } from '@utils/post-fetch-with-renewal';
import { HttpError } from '@utils/request';
// Deleting posts the same request the assignment form builds, so the body has
// one definition.
import { toPrincipalPayload } from '@/components/AssignmentForm/useAssignmentForm';

/**
 * A returned item either scopes a role to an object, or carries a role on its
 * own — a role the app holds outright rather than over something.
 */
type AppItem = {
	objectType?: string;
	objectId?: string;
	role: string;
};

/** An item scoped to an object, which is what the table lists. */
export type AppAssignment = Required<AppItem>;

const isAssignment = (item: AppItem): item is AppAssignment =>
	!!item.objectType && !!item.objectId;

type AppsResponse = {
	items: AppItem[];
	/** Cursor for the next page; null when there are no more. Unused for now. */
	nextCursor: string | null;
};

export const APPS_PAGE_SIZE = 50;

const useAppsPage = () => {
	const token = useToken();
	const { oktaAuth } = useOktaAuth();
	const { message, modal } = App.useApp();
	const queryClient = useQueryClient();

	// The searched client id, as opposed to what's currently typed.
	const [cid, setCid] = useState('');

	useTopbar(<Breadcrumbs />);

	const {
		data,
		isFetching,
		error,
	} = useQuery({
		queryKey: ['apps', cid],
		queryFn: () =>
			getFetch<AppsResponse>(token, `${APPS_API_URL}/${encodeURIComponent(cid)}`, {
				pageSize: APPS_PAGE_SIZE,
			}),
		// Nothing to look up until a client id has been searched for.
		enabled: !!cid && !!token && !!AUTH_SERVICES_BASE_URL,
		// Don't retry auth failures (global default is retry: 3).
		retry: (failureCount, err) =>
			!(err instanceof HttpError && (err.status === 401 || err.status === 403)) &&
			failureCount < 3,
	});

	// useQuery has no onError in v5, so surface the failure here rather than
	// letting it show up as an empty table.
	useEffect(() => {
		if (error) {
			message.error(`Could not load apps: ${error.message}`);
		}
	}, [error, message]);

	const {
		mutate: deleteAssignment,
		isPending: isDeletingAny,
		variables: deleting,
	} = useMutation({
		mutationFn: async (assignment: AppAssignment) => {
			if (!AUTH_SERVICES_BASE_URL) {
				throw new Error('Auth services URL is not configured');
			}

			return await postFetchWithRenewal<unknown>(
				oktaAuth,
				token,
				PRINCIPALS_API_URL,
				toPrincipalPayload({
					principalType: 'app',
					principal: cid,
					operationType: 'delete',
					objects: [assignment],
				})
			);
		},
		onSuccess: () => {
			message.success('Assignment deleted');
			queryClient.invalidateQueries({ queryKey: ['apps', cid] });
		},
		onError: (err) => {
			message.error(err instanceof Error ? err.message : 'Request failed');
		},
	});

	/** Revoking an app's access is worth a second look before it happens. */
	const confirmDeleteAssignment = (assignment: AppAssignment) => {
		modal.confirm({
			title: 'Delete this assignment?',
			content: `${cid} will lose the ${assignment.role} role on ${assignment.objectId}.`,
			okText: 'Delete',
			okButtonProps: { danger: true },
			cancelText: 'Keep',
			onOk: () => deleteAssignment(assignment),
		});
	};

	const search = (value: string) => {
		setCid(value.trim());
	};

	const items = data?.items ?? [];

	return {
		cid,
		search,
		items: items.filter(isAssignment),
		// Roles held outright are shown as tags instead of table rows. Deduped,
		// since the same role twice would just look like a bug.
		roles: [...new Set(items.filter((item) => !isAssignment(item)).map(({ role }) => role))],
		isFetching,
		hasSearched: !!cid,
		confirmDeleteAssignment,
		isDeleting: (assignment: AppAssignment) => isDeletingAny && deleting === assignment,
	};
};

export default useAppsPage;
